import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CholesterolMedicationClasses, BloodPressureMedicationClasses } from "./data";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { capitalizeFirstLetter } from "../../../../../helpers/functions";

const getCurrentTabType = (currentTabName) => {
  if (!currentTabName) return "default";
  const tabLower = currentTabName.toLowerCase();

  if (tabLower.includes("cholesterol")) return "cholesterol";
  if (tabLower.includes("blood") || tabLower.includes("pressure")) return "bloodPressure";
  return "default";
};

const filterByAllowedClasses = (medications, allowedClassNames, includeNameMatch = false) => {
  if (!Array.isArray(medications) || medications.length === 0) return [];

  const allowedClassSet = new Set(allowedClassNames);
  const allowedClassNamesLower = includeNameMatch
    ? allowedClassNames.map((className) => className.toLowerCase())
    : [];

  return medications.filter((drug) => {
    const className = drug?.class?.className;
    const hasAllowedClass = className && allowedClassSet.has(className);
    if (hasAllowedClass) return true;

    if (!includeNameMatch) return false;
    const name = drug?.name?.toLowerCase() || "";
    return allowedClassNamesLower.some((allowed) => name.includes(allowed));
  });
};

const getFilteredDrugMedications = (amrEducation, currentTabName) => {
  if (!Array.isArray(amrEducation) || amrEducation.length === 0) return [];

  const tabType = getCurrentTabType(currentTabName);

  if (tabType === "cholesterol") {
    const allowed = CholesterolMedicationClasses.map((item) => item.className);
    return filterByAllowedClasses(amrEducation, allowed, true);
  }

  if (tabType === "bloodPressure") {
    const allowed = BloodPressureMedicationClasses.map((item) => item.className);
    return filterByAllowedClasses(amrEducation, allowed);
  }

  return amrEducation;
};

const DrugCardUI = ({ iconUrl = "", headerBg = "bg-teal-75", borderClass = "border-teal-100", headerTextClass = null, selectedBorderClass = "border-REELTeal", currentTabName = null }) => {
  const { amrEducation } = useSelector((state) => state.clinicalService);

  const [selectedDrugInfor, setSelectedDrugInfor] = useState(null);
  const [selectedDrug, setSelectedDrug] = useState(null);

  const drugMedications = useMemo(
    () => getFilteredDrugMedications(amrEducation, currentTabName),
    [amrEducation, currentTabName]
  );

  const isSingleMedication = drugMedications.length === 1;

  return (
    <>
      {drugMedications.length > 0 ?
        drugMedications.map((drug, index) => {
          const isInfoOpen = selectedDrugInfor === drug.name;
          const isSelected = selectedDrug === drug.name;
          const cardHeaderBg = isInfoOpen ? "bg-teal-25" : headerBg;
          const headerTextColor = headerTextClass
            ? headerTextClass
            : isInfoOpen
            ? "text-REELTeal"
            : "text-gray-800";
          const medicationName = capitalizeFirstLetter(drug?.name?.split(" ")[0] || "");
          const className = drug?.class?.className || "";
          const worksIn = drug?.class?.worksIn?.toUpperCase() || "";
          const description = drug?.class?.description || "";
          const key = `${drug?.name || "drug"}-${className || "class"}-${index}`;

          return (
          <div key={key} className={`flex shrink-0 flex-row ${isSingleMedication ? 'pl-0 mx-auto' : 'pl-0'}`}>
            <div className={isSingleMedication ? 'w-auto' : 'w-auto shrink-0'}>
              <div
                className="relative flex min-w-[480px] flex-row justify-center"
                onClick={() => {
                  setSelectedDrug(drug.name);
                }}
              >
                <div className={`absolute flex ${isInfoOpen ? '-left-10 top-0' : 'left-1/2 -translate-x-1/2'}`}>
                    <img
                      src={`/icons/${iconUrl}${isSelected ? "-dark" : ""}.svg`}
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (!img.dataset.fallback) {
                          img.dataset.fallback = "1";
                          img.src = `/icons/${iconUrl}.svg`;
                        }
                      }}
                      className="flex justify-center opacity-100"
                      alt=""
                    />
                </div>
                <div className={`flex w-full flex-col justify-center items-center border-4 pb-3 rounded-[20px] mt-14 shadow-lg shadow-gray-400 
                ${ isSelected ? selectedBorderClass : borderClass}`}>
                    <div className={`flex w-full ${isInfoOpen ? 'justify-center items-center' : 'justify-center items-center'} ${cardHeaderBg} border-b-4 pt-16 pb-3 rounded-t-[20px]
                    ${ isSelected ? selectedBorderClass : borderClass}`}>
                    <p className={`text-[26px] ${isInfoOpen ? 'font-bold' : 'font-semibold'} ${headerTextColor}`}>
                      {isInfoOpen ? medicationName : className}
                    </p>
                  </div>
                  {!isInfoOpen && (
                    <div className="flex min-h-[220px] flex-col justify-center items-center py-3 gap-[40px]">
                      <p className={`text-[24px] font-bold ${headerTextClass ? headerTextClass : 'text-REELTeal'} -mt-[10px]`}>
                        {medicationName}
                      </p>
                      <div
                        className="min-w-[420px] bg-gray-20 flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          setSelectedDrugInfor(drug.name);
                          setSelectedDrug(drug.name);
                        }}
                      >
                        <h3 className="text-[20px] font-semibold text-gray-800">How does it work?</h3>
                        <ChevronRightIcon className="w-6 h-6 text-teal-150" />
                      </div>
                    </div>
                  )}
                  {isInfoOpen && (
                    <div className="flex min-h-[220px] flex-col pt-4 gap-2 ">
                      <span className="flex flex-col text-teal-300 gap-4">
                        <p className="flex text-[22px] font-semibold justify-start items-center gap-2">
                          <ChevronLeftIcon 
                            className="w-6 h-6 text-teal-150 cursor-pointer hover:text-gray-900 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDrugInfor(null);
                            }}
                          />
                          How does it work?
                        </p>
                        <p className="flex max-w-[420px] justify-start items-start text-xl font-medium whitespace-pre-line">
                          {description}
                        </p>
                      </span>
                    </div>
                  )}
                  <div className="flex flex-row w-full justify-center items-center px-[10px] text-lg text-gray-800 gap-[2px]">
                    <span className="flex flex-row min-w-full justify-center items-center pt-2 border-t-[1px] border-gray-100 gap-[10px]">
                      <p className="flex font-medium">{"Works in the"} </p>
                      <p className="flex font-bold">
                        {worksIn}
                      </p>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })
      :
      <><h1 className="h-[29px] font-bold text-2xl p-10 text-gray-600">
      No Medication Found.
    </h1></>}
    </>
  );
};

export default DrugCardUI;
